import React, { useState, useEffect, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Autocomplete } from '@react-google-maps/api';
import { Settings, MapPin, Clock, Wrench, XCircle, AlertTriangle, Zap, CheckCircle, Calendar, Timer, Star, Save, Bookmark, Edit3, X } from 'lucide-react';
import { config, loadUserSettings, UserSettings, BoothSetting, SavedLocation, saveUserSettings } from './config';
import SettingsModal from './SettingsModal';
import './App.css';

const libraries: ("places" | "geometry")[] = ["places", "geometry"];

const containerStyle = {
  width: '100%',
  height: '240px'
};

// Randburg, South Africa coordinates
const center = {
  lat: -26.0939,
  lng: 28.0061
};

// Generate hour and minute options
const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

function App() {
  const [userSettings, setUserSettings] = useState<UserSettings>(loadUserSettings());
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  
  // Location states
  const [location1, setLocation1] = useState('');
  const [location2, setLocation2] = useState('');
  const [autocomplete1, setAutocomplete1] = useState<google.maps.places.Autocomplete | null>(null);
  const [autocomplete2, setAutocomplete2] = useState<google.maps.places.Autocomplete | null>(null);
  
  // Time states - using separate hour/minute
  const [event1EndHour, setEvent1EndHour] = useState('17');
  const [event1EndMinute, setEvent1EndMinute] = useState('00');
  const [event2StartHour, setEvent2StartHour] = useState('19');
  const [event2StartMinute, setEvent2StartMinute] = useState('00');
  
  // Booth states
  const [event1BoothType, setEvent1BoothType] = useState('inflatable');
  const [event2BoothType, setEvent2BoothType] = useState('inflatable');
  
  // Map states
  const [directionsResponse, setDirectionsResponse] = useState<google.maps.DirectionsResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{
    status: string;
    message: string;
    details: any[];
    color: string;
    icon: any;
  } | null>(null);

  // Bookmark states
  const [showBookmarks1, setShowBookmarks1] = useState(false);
  const [showBookmarks2, setShowBookmarks2] = useState(false);
  const [showSaveDialog1, setShowSaveDialog1] = useState(false);
  const [showSaveDialog2, setShowSaveDialog2] = useState(false);
  const [saveLocationName, setSaveLocationName] = useState('');
  const [editingBookmark, setEditingBookmark] = useState<string | null>(null);
  const [editBookmarkName, setEditBookmarkName] = useState('');
  const [editBookmarkAddress, setEditBookmarkAddress] = useState('');

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMapsApiKey,
    libraries: libraries
  });

  // Apply theme and background color
  useEffect(() => {
    document.body.className = userSettings.theme === 'dark' ? 'dark-theme' : 'light-theme';
    document.body.style.backgroundColor = userSettings.backgroundColor || '#ffffff';
  }, [userSettings.theme, userSettings.backgroundColor]);

  // Close bookmark dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.bookmark-dropdown')) {
        setShowBookmarks1(false);
        setShowBookmarks2(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
  };
  }, []);



  const onLoad1 = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete1(autocomplete);
  }, []);

  const onLoad2 = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    setAutocomplete2(autocomplete);
  }, []);

  const onPlaceChanged1 = () => {
    if (autocomplete1 !== null) {
      const place = autocomplete1.getPlace();
      setLocation1(place.formatted_address || '');
    }
  };

  const onPlaceChanged2 = () => {
    if (autocomplete2 !== null) {
      const place = autocomplete2.getPlace();
      setLocation2(place.formatted_address || '');
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const calculateRoute = async () => {
    if (!location1 || !location2 || !event1BoothType || !event2BoothType) {
      alert('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    
    try {
      const directionsService = new google.maps.DirectionsService();
      
      // Calculate route with current traffic conditions
      const results = await directionsService.route({
        origin: location1,
        destination: location2,
        travelMode: google.maps.TravelMode.DRIVING,
        drivingOptions: {
          departureTime: new Date(),
          trafficModel: google.maps.TrafficModel.BEST_GUESS
        },
        avoidHighways: false,
        avoidTolls: false
      });

      setDirectionsResponse(results);
      
      // Get travel time in minutes (with current traffic)
      const leg = results.routes[0].legs[0];
      const travelTimeMinutes = Math.ceil((leg.duration_in_traffic?.value || leg.duration?.value || 0) / 60);
      
      // Get booth configurations
      const event1Booth = userSettings.booths[event1BoothType];
      const event2Booth = userSettings.booths[event2BoothType];
      
      if (!event1Booth || !event2Booth) {
        throw new Error('Invalid booth configuration');
      }
      
      // Calculate times
      const event1End = new Date(`2024-01-01T${event1EndHour}:${event1EndMinute}:00`);
      const event2Start = new Date(`2024-01-01T${event2StartHour}:${event2StartMinute}:00`);
      
      // Handle next day scenario
      if (event2Start < event1End) {
        event2Start.setDate(event2Start.getDate() + 1);
      }
      
      const availableTimeMinutes = (event2Start.getTime() - event1End.getTime()) / (1000 * 60);
      const requiredTimeMinutes = event1Booth.breakdownTime + travelTimeMinutes + event2Booth.setupTime;
      const bufferMinutes = availableTimeMinutes - requiredTimeMinutes;
      
      let status, message, color, icon;
      
      if (bufferMinutes < 0) {
        status = "Won't Make It";
        message = `You're ${Math.abs(bufferMinutes)} minutes short. Consider adjusting the schedule.`;
        color = '#dc2626';
        icon = XCircle;
      } else if (bufferMinutes <= 15) {
        status = "Very Tight";
        message = "Cutting it very close! No room for delays.";
        color = '#ea580c';
        icon = AlertTriangle;
      } else if (bufferMinutes <= 30) {
        status = "Just Makes It";
        message = "Doable but tight. Plan for efficiency.";
        color = '#f59e0b';
        icon = Zap;
      } else {
        status = "Enough Time";
        message = "Comfortable timing with buffer for unexpected delays.";
        color = '#059669';
        icon = CheckCircle;
      }
      
      const details = [
        { 
          label: 'Time Available', 
          value: formatTime(Math.round(availableTimeMinutes)),
          icon: Timer,
          color: '#059669',
          isHeader: true
        },
        { 
          label: 'Breakdown Time', 
          value: formatTime(event1Booth.breakdownTime),
          icon: Wrench,
          color: '#ea580c'
        },
        { 
          label: 'Travel Time', 
          value: formatTime(travelTimeMinutes),
          icon: MapPin,
          color: '#2563eb'
        },
        { 
          label: 'Setup Time', 
          value: formatTime(event2Booth.setupTime),
          icon: Settings,
          color: '#16a34a'
        },
        { 
          label: 'Total Time Needed', 
          value: formatTime(requiredTimeMinutes),
          isSubtotal: true
        },
        { 
          label: 'Time Left Over', 
          value: bufferMinutes >= 0 ? formatTime(bufferMinutes) : `-${formatTime(Math.abs(bufferMinutes))}`,
          icon: bufferMinutes >= 0 ? CheckCircle : XCircle,
          color: bufferMinutes >= 0 ? '#059669' : '#dc2626',
          isTotal: true
        }
      ];
      
      setResult({ status, message, details, color, icon });
      
    } catch (error) {
      console.error('Error calculating route:', error);
      alert('Error calculating route. Please check your locations.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsUpdate = (newSettings: UserSettings) => {
    setUserSettings(newSettings);
  };

  const saveLocation = (locationAddress: string, locationName: string) => {
    if (!locationAddress.trim()) {
      alert('Please enter a location first');
      return;
    }

    const newLocation: SavedLocation = {
      id: Date.now().toString(),
      name: locationName.trim() || locationAddress,
      address: locationAddress,
      createdAt: Date.now()
    };

    const newSettings = {
      ...userSettings,
      savedLocations: [...userSettings.savedLocations, newLocation]
    };

    setUserSettings(newSettings);
    saveUserSettings(newSettings);
    setSaveLocationName('');
    setShowSaveDialog1(false);
    setShowSaveDialog2(false);
  };

  const loadBookmarkedLocation = (location: SavedLocation, locationNumber: 1 | 2) => {
    if (locationNumber === 1) {
      setLocation1(location.address);
      setShowBookmarks1(false);
    } else {
      setLocation2(location.address);
      setShowBookmarks2(false);
    }
  };

  const deleteBookmark = (locationId: string) => {
    const newSettings = {
      ...userSettings,
      savedLocations: userSettings.savedLocations.filter(loc => loc.id !== locationId)
    };
    setUserSettings(newSettings);
    saveUserSettings(newSettings);
  };

  const isLocationSaved = (currentLocation: string) => {
    return userSettings.savedLocations.some(
      savedLocation => savedLocation.address.toLowerCase().trim() === currentLocation.toLowerCase().trim()
    );
  };

  const startEditingBookmark = (location: SavedLocation) => {
    setEditingBookmark(location.id);
    setEditBookmarkName(location.name);
    setEditBookmarkAddress(location.address);
  };

  const saveBookmarkEdit = () => {
    if (!editingBookmark) return;
    
    const updatedSettings = {
      ...userSettings,
      savedLocations: userSettings.savedLocations.map(location =>
        location.id === editingBookmark
          ? { ...location, name: editBookmarkName.trim(), address: editBookmarkAddress.trim() }
          : location
      )
    };
    
    setUserSettings(updatedSettings);
    saveUserSettings(updatedSettings);
    setEditingBookmark(null);
    setEditBookmarkName('');
    setEditBookmarkAddress('');
  };

  const cancelBookmarkEdit = () => {
    setEditingBookmark(null);
    setEditBookmarkName('');
    setEditBookmarkAddress('');
  };

  if (!isLoaded) {
    return <div className="loading">Loading Google Maps...</div>;
  }

  const boothOptions = Object.entries(userSettings.booths);

  return (
    <div className={`app ${userSettings.theme}`}>
      <div className="container">
        {/* Header */}
        <div className="header">
          <div className="header-content">
            <h1 className="main-title">Will They Make It?</h1>
            <p className="subtitle">
              Calculate if you can make it between two events with your photo booth
            </p>
          </div>
          <div className="header-controls">
            <button
              onClick={() => setIsSettingsOpen(true)}
              className="settings-button"
              title="Settings"
            >
              <Settings size={18} />
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="content-wrapper">
        <div className="main-grid">
          {/* Left Column */}
          <div className="left-column">
            {/* Locations Section */}
            <div className="section locations-section">
              <div className="section-header">
                <MapPin size={20} />
                <h2>Locations</h2>
              </div>
              <div className="input-group">
                <label>Event 1 Location (Starting Point)</label>
                <div className="location-input-container">
                  <div className="location-input-wrapper">
                <Autocomplete onLoad={onLoad1} onPlaceChanged={onPlaceChanged1}>
                  <input
                    type="text"
                    value={location1}
                    onChange={(e) => setLocation1(e.target.value)}
                    placeholder="Enter starting location"
                    className="location-input"
                  />
                </Autocomplete>
                    {location1 && (
                      <button
                        onClick={() => setLocation1('')}
                        className="location-clear-button"
                        title="Clear location"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="bookmark-buttons">
                    <div className="bookmark-dropdown">
                                          <button
                      onClick={() => setShowBookmarks1(!showBookmarks1)}
                      className={`bookmark-button ${isLocationSaved(location1) ? 'bookmark-saved' : ''}`}
                      title="Saved Locations"
                    >
                      <Star size={16} fill={isLocationSaved(location1) ? '#fbbf24' : 'none'} />
                    </button>
                      {showBookmarks1 && (
                        <div className="bookmark-dropdown-content">
                          {userSettings.savedLocations.length === 0 ? (
                            <div className="no-bookmarks">No saved locations</div>
                          ) : (
                            userSettings.savedLocations.map((location) => (
                              <div key={location.id} className="bookmark-item">
                                {editingBookmark === location.id ? (
                                  <div className="bookmark-edit-form">
                                    <div className="bookmark-edit-field">
                                      <label>Name:</label>
                                      <div className="bookmark-edit-input-container">
                                        <input
                                          type="text"
                                          value={editBookmarkName}
                                          onChange={(e) => setEditBookmarkName(e.target.value)}
                                          className="bookmark-edit-input"
                                          placeholder="Location name"
                                        />
                                        <button
                                          onClick={() => setEditBookmarkName('')}
                                          className="bookmark-clear-button"
                                          title="Clear field"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="bookmark-edit-field">
                                      <label>Address:</label>
                                      <div className="bookmark-edit-input-container">
                                        <input
                                          type="text"
                                          value={editBookmarkAddress}
                                          onChange={(e) => setEditBookmarkAddress(e.target.value)}
                                          className="bookmark-edit-input"
                                          placeholder="Full address"
                                        />
                                        <button
                                          onClick={() => setEditBookmarkAddress('')}
                                          className="bookmark-clear-button"
                                          title="Clear field"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="bookmark-edit-actions">
                                      <button
                                        onClick={cancelBookmarkEdit}
                                        className="bookmark-cancel-button"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={saveBookmarkEdit}
                                        className="bookmark-save-button"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => loadBookmarkedLocation(location, 1)}
                                      className="bookmark-load-button"
                                      title={location.address}
                                    >
                                      <MapPin size={14} />
                                      <span>{location.name}</span>
                                    </button>
                                    <button
                                      onClick={() => startEditingBookmark(location)}
                                      className="bookmark-edit-button"
                                      title="Edit bookmark"
                                    >
                                      <Edit3 size={12} />
                                    </button>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setShowSaveDialog1(true)}
                      className="save-button"
                      title="Save Location"
                    >
                      <Save size={16} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="input-group">
                <label>Event 2 Location (Destination)</label>
                <div className="location-input-container">
                  <div className="location-input-wrapper">
                <Autocomplete onLoad={onLoad2} onPlaceChanged={onPlaceChanged2}>
                  <input
                    type="text"
                    value={location2}
                    onChange={(e) => setLocation2(e.target.value)}
                    placeholder="Enter destination location"
                    className="location-input"
                  />
                </Autocomplete>
                    {location2 && (
                      <button
                        onClick={() => setLocation2('')}
                        className="location-clear-button"
                        title="Clear location"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  <div className="bookmark-buttons">
                    <div className="bookmark-dropdown">
                                          <button
                      onClick={() => setShowBookmarks2(!showBookmarks2)}
                      className={`bookmark-button ${isLocationSaved(location2) ? 'bookmark-saved' : ''}`}
                      title="Saved Locations"
                    >
                      <Star size={16} fill={isLocationSaved(location2) ? '#fbbf24' : 'none'} />
                    </button>
                      {showBookmarks2 && (
                        <div className="bookmark-dropdown-content">
                          {userSettings.savedLocations.length === 0 ? (
                            <div className="no-bookmarks">No saved locations</div>
                          ) : (
                            userSettings.savedLocations.map((location) => (
                              <div key={location.id} className="bookmark-item">
                                {editingBookmark === location.id ? (
                                  <div className="bookmark-edit-form">
                                    <div className="bookmark-edit-field">
                                      <label>Name:</label>
                                      <div className="bookmark-edit-input-container">
                                        <input
                                          type="text"
                                          value={editBookmarkName}
                                          onChange={(e) => setEditBookmarkName(e.target.value)}
                                          className="bookmark-edit-input"
                                          placeholder="Location name"
                                        />
                                        <button
                                          onClick={() => setEditBookmarkName('')}
                                          className="bookmark-clear-button"
                                          title="Clear field"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="bookmark-edit-field">
                                      <label>Address:</label>
                                      <div className="bookmark-edit-input-container">
                                        <input
                                          type="text"
                                          value={editBookmarkAddress}
                                          onChange={(e) => setEditBookmarkAddress(e.target.value)}
                                          className="bookmark-edit-input"
                                          placeholder="Full address"
                                        />
                                        <button
                                          onClick={() => setEditBookmarkAddress('')}
                                          className="bookmark-clear-button"
                                          title="Clear field"
                                        >
                                          <X size={12} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="bookmark-edit-actions">
                                      <button
                                        onClick={cancelBookmarkEdit}
                                        className="bookmark-cancel-button"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={saveBookmarkEdit}
                                        className="bookmark-save-button"
                                      >
                                        Save
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => loadBookmarkedLocation(location, 2)}
                                      className="bookmark-load-button"
                                      title={location.address}
                                    >
                                      <MapPin size={14} />
                                      <span>{location.name}</span>
                                    </button>
                                    <button
                                      onClick={() => startEditingBookmark(location)}
                                      className="bookmark-edit-button"
                                      title="Edit bookmark"
                                    >
                                      <Edit3 size={12} />
                                    </button>
                                  </>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => setShowSaveDialog2(true)}
                      className="save-button"
                      title="Save Location"
                    >
                      <Save size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Event Cards Grid */}
            <div className="events-grid">
              {/* Event 1 Card */}
              <div className="section event-card event1-card">
                <div className="section-header">
                  <Calendar size={18} />
                  <h2>Event 1</h2>
                </div>
                
                <div className="input-group">
                  <label>End Time</label>
                  <div className="time-selector">
                    <select
                      value={event1EndHour}
                      onChange={(e) => setEvent1EndHour(e.target.value)}
                      className="time-input"
                    >
                      {hours.map(hour => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </select>
                    <span className="time-separator">:</span>
                    <select
                      value={event1EndMinute}
                      onChange={(e) => setEvent1EndMinute(e.target.value)}
                      className="time-input"
                    >
                      {minutes.map(minute => (
                        <option key={minute} value={minute}>{minute}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Booth Type</label>
                  <select
                    value={event1BoothType}
                    onChange={(e) => setEvent1BoothType(e.target.value)}
                    className="booth-select"
                  >
                    <option value="">Select booth type</option>
                    {boothOptions.map(([key, booth]) => (
                      <option key={key} value={key}>
                        {booth.name}
                      </option>
                    ))}
                  </select>
                  <div className="booth-times-container">
                    {event1BoothType && userSettings.booths[event1BoothType] && (
                      <div className="booth-times">
                        <Wrench size={12} />
                        Setup: {userSettings.booths[event1BoothType].setupTime}min | 
                        Breakdown: {userSettings.booths[event1BoothType].breakdownTime}min
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Event 2 Card */}
              <div className="section event-card event2-card">
                <div className="section-header">
                  <Calendar size={18} />
                  <h2>Event 2</h2>
                </div>
                
                <div className="input-group">
                  <label>Start Time</label>
                  <div className="time-selector">
                    <select
                      value={event2StartHour}
                      onChange={(e) => setEvent2StartHour(e.target.value)}
                      className="time-input"
                    >
                      {hours.map(hour => (
                        <option key={hour} value={hour}>{hour}</option>
                      ))}
                    </select>
                    <span className="time-separator">:</span>
                    <select
                      value={event2StartMinute}
                      onChange={(e) => setEvent2StartMinute(e.target.value)}
                      className="time-input"
                    >
                      {minutes.map(minute => (
                        <option key={minute} value={minute}>{minute}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="input-group">
                  <label>Booth Type</label>
                  <select
                    value={event2BoothType}
                    onChange={(e) => setEvent2BoothType(e.target.value)}
                    className="booth-select"
                  >
                    <option value="">Select booth type</option>
                    {boothOptions.map(([key, booth]) => (
                      <option key={key} value={key}>
                        {booth.name}
                      </option>
                    ))}
                  </select>
                  <div className="booth-times-container">
                    {event2BoothType && userSettings.booths[event2BoothType] && (
                      <div className="booth-times">
                        <Wrench size={12} />
                        Setup: {userSettings.booths[event2BoothType].setupTime}min | 
                        Breakdown: {userSettings.booths[event2BoothType].breakdownTime}min
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Route Information Card */}
              <div className="section route-info-card">
                <div className="section-header">
                  <MapPin size={18} />
                  <h2>Route Information</h2>
                </div>
                <div className="route-info-content">
                {directionsResponse ? (
                  <>
                  <p>
                    <strong>Distance:</strong> {directionsResponse.routes[0].legs[0].distance?.text || 'Unknown'}
                  </p>
                  <p className="traffic-note">
                    * Travel time includes current traffic conditions
                  </p>
                  </>
                ) : (
                  <p className="no-route-text">Route information will appear here after calculation</p>
                )}
              </div>
            </div>

            {/* Calculate Button */}
            <button
              onClick={calculateRoute}
              disabled={isLoading}
              className="calculate-button"
            >
              {isLoading ? 'Calculating...' : 'Calculate'}
            </button>
          </div>

          {/* Right Column */}
          <div className="right-column">
            {/* Map */}
            <div className="map-container">
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={10}
                options={{
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                {directionsResponse && (
                  <DirectionsRenderer
                    directions={directionsResponse}
                    options={{
                      suppressMarkers: false,
                      polylineOptions: {
                        strokeColor: '#3b82f6',
                        strokeWeight: 4,
                      },
                    }}
                  />
                )}
              </GoogleMap>
            </div>

            {/* Results */}
            {result && (
              <div className="result-container" style={{ borderColor: result.color }}>
                <div className="result-status" style={{ color: result.color }}>
                  <result.icon size={24} />
                  {result.status}
                </div>
                <div className="result-message">
                  {result.message}
                </div>
                <div className="result-details">
                  <h4>Time Breakdown</h4>
                  {result.details.map((detail, index) => (
                    <div key={index} className={`detail-line ${detail.isHeader ? 'detail-header' : ''} ${detail.isSubtotal ? 'detail-subtotal' : ''} ${detail.isTotal ? 'detail-total' : ''}`}>
                      <span className="detail-label">
                        {detail.icon && <detail.icon size={16} className="detail-icon" style={{ color: detail.color }} />}
                        {detail.label}
                      </span>
                      <span className="detail-value" style={{ color: detail.color || 'inherit' }}>
                        {detail.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!result && (
              <div className="empty-state">
                <MapPin size={48} className="empty-state-icon" />
                <p>Results will appear here after calculation</p>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* Save Location Dialogs */}
      {(showSaveDialog1 || showSaveDialog2) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            maxWidth: '320px',
            width: '100%',
            margin: '0 1rem',
            border: '2px solid #bfdbfe'
          }}>
            <h3 style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#1e40af',
              margin: '0 0 1rem 0',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Save size={20} />
              Save Location
            </h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{
                display: 'block',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '0.5rem'
              }}>
                Location Name (optional)
              </label>
              <input
                type="text"
                value={saveLocationName}
                onChange={(e) => setSaveLocationName(e.target.value)}
                placeholder={showSaveDialog1 ? location1 : location2}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid #bfdbfe',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    saveLocation(showSaveDialog1 ? location1 : location2, saveLocationName);
                  }
                }}
                autoFocus
              />
              <p style={{
                fontSize: '0.75rem',
                color: '#6b7280',
                margin: '0.5rem 0 0 0'
              }}>
                Leave empty to use the full address as the name
              </p>
            </div>
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setSaveLocationName('');
                  setShowSaveDialog1(false);
                  setShowSaveDialog2(false);
                }}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => saveLocation(showSaveDialog1 ? location1 : location2, saveLocationName)}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        userSettings={userSettings}
        onSettingsUpdate={handleSettingsUpdate}
      />
    </div>
  );
}

export default App;
